import { lazyWithRetry as lazy } from '@utils/lazyWithRetry';
import {
  contractorDashboardDetailRoutes,
  contractorMonthlyReportRoutes,
} from '@component/LightVersion/Reports/rideshare-reports/helpers';
import { memberHideDashboardRoutes } from '@component/LightVersion/Reports/member-reports/helpers';
import { rideHideDashboardRoutes } from '@component/LightVersion/Reports/ride-reports/constants';
import { RouteConfig } from '@routes/types';

const HomeWrapper = lazy(() => import('@page/HomeWrapper'));
const PageWrapper = lazy(() => import('@page/PageWrapper'));
const PageNotFound = lazy(() => import('@component/PageNotFound/PageNotFound'));
const IconsWrapper = lazy(() => import('@page/IconsWrapper'));
const ConfigWrapper = lazy(() => import('@page/ConfigWrapper'));
const SomethingWentWrong = lazy(
  () => import('@component/SomethingWentWrong/SomethingWentWrong')
);
const NoAuthorization = lazy(
  () => import('@component/NoAuthorization/NoAuthorization')
);

const PayerInfoLight = lazy(
  () => import('@component/LightVersion/Payer/PayerInfoLight/PayerInfoLight')
);
const PayerFeatures = lazy(
  () => import('@component/LightVersion/Payer/PayerFeatures/PayerFeatures')
);
const PayerServices = lazy(
  () => import('@component/LightVersion/Payer/PayerServices/PayerServicesLight')
);

const PayerSettings = lazy(
  () => import('@component/LightVersion/Payer/PayerSettings/PayerSettings')
);

const MembersInfoLight = lazy(
  () =>
    import('@component/LightVersion/Members/MembersInfoLight/MembersInfoLight')
);
const MemberBenefits = lazy(
  () => import('@component/LightVersion/Members/MemberBenefits/MemberBenefits')
);
const MyRoomPreferences = lazy(
  () => import('@component/Member/MyRoomPreferences/MyRoomPreferences')
);

const ContractorsInfoLight = lazy(
  () =>
    import(
      '@component/LightVersion/Contractors/ContractorsInfoLight/ContractorsInfoLight'
    )
);
const CreateContractorFlow = lazy(
  () => import('@component/CreateContractorFlow/CreateContractorFlow')
);
const ContractorsOperationsLight = lazy(
  () =>
    import(
      '@component/LightVersion/Contractors/ContractorsOperationsLight/ContractorsOperationsLight'
    )
);

const Assignment = lazy(() => import('@component/Assignment/Assignment'));
const AuthContactsLightNew = lazy(
  () =>
    import('@component/LightVersion/AuthContactsLightNew/AuthContactsLightNew')
);
const Monitoring = lazy(() => import('@component/Monitoring/Monitoring'));

const RidesPage = lazy(() => import('@component/RidesPage/RidesPage'));
const RidesDetails = lazy(() => import('@component/Ride/RidesDetails'));
const StandingOrder = lazy(
  () => import('@component/Ride/StandingOrder/StandingOrder')
);
const QualityIncidents = lazy(
  () => import('@component/QualityIncidents/QualityIncidents')
);

const Users = lazy(() => import('@component/Users/Users'));
const Permissions = lazy(() => import('@component/Permissions/Permissions'));
const MyRoomAuditLogs = lazy(
  () => import('@component/AuditLog/MyRoomAuditLogs/MyRoomAuditLogs')
);
const ProfileAuditLogs = lazy(
  () => import('@component/Profile/ProfileAuditLogs')
);
const ProfilePermissions = lazy(
  () => import('@component/Profile/ProfilePermissions/ProfilePermissions'))
const ProfileRecentActivity = lazy(
  () =>
    import('@component/Profile/ProfileRecentActivity/ProfileRecentActivity')
);
const ProfileUserProfile = lazy(
  () => import('@component/Profile/ProfileUserProfile')
);

const MemberReports = lazy(
  () => import('@component/LightVersion/Reports/member-reports/MemberReports')
);
const RideshareReports = lazy(
  () =>
    import(
      '@component/LightVersion/Reports/rideshare-reports/RideshareReportsDashboard'
    )
);
const RideReports = lazy(
  () => import('@component/LightVersion/Reports/ride-reports/RideReports')
);
const ContractorRejectedReports = lazy(
  () => import('@src/components/LightVersion/Reports/ContractorRejectedReports')
);

const RideTables = lazy(
  () => import('@src/components/LightVersion/Reports/ride-reports/ride-tables')
);
const RideshareTables = lazy(
  () =>
    import(
      '@src/components/LightVersion/Reports/rideshare-reports/rideshare-tables'
    )
);
const RideshareSeeRidesTable = lazy(
  () =>
    import(
      '@src/components/LightVersion/Reports/rideshare-reports/rideshare-see-rides-table'
    )
);
const ContractorMonthlyReportTable = lazy(
  () =>
    import(
      '@src/components/LightVersion/Reports/rideshare-reports/ContractorMonthlyReportTable'
    )
);
const MemberTables = lazy(
  () => import('@component/LightVersion/Reports/member-reports/member-tables')
);

export const rideshareRoutes: RouteConfig[] = [
  {
    path: '/',
    element: HomeWrapper,
    children: [
      {
        path: 'payors',
        element: PageWrapper,
        children: [
          { path: 'information/:id', element: PayerInfoLight },
          { path: 'features/:id', element: PayerFeatures },
          { path: 'services/:id', element: PayerServices },
          { path: 'settings/:id', element: PayerSettings },
          { path: 'audit-logs/:id', element: MyRoomAuditLogs },
        ],
      },
      {
        path: 'members',
        element: PageWrapper,
        children: [
          { path: 'information/:id', element: MembersInfoLight },
          { path: 'preferences/:id', element: MyRoomPreferences },
          { path: 'contacts/:id', element: AuthContactsLightNew },
          { path: 'benefits/:id', element: MemberBenefits },
          { path: 'rides/:id', element: RidesPage },
          { path: 'audit-logs/:id', element: MyRoomAuditLogs },
        ],
      },
      {
        path: 'administrator',
        element: PageWrapper,
        children: [
          { path: 'users', element: Users },
          { path: 'permissions', element: Permissions },
        ],
      },
      {
        path: 'rides',
        element: PageWrapper,
        children: [
          { path: 'information', element: RidesDetails },
          { path: 'information/:id/*', element: RidesDetails },
          { path: 'standing-order', element: StandingOrder },
          { path: 'standing-order/:id', element: StandingOrder },
          { path: 'contacts/:id', element: AuthContactsLightNew },
          { path: 'quality-incidents', element: QualityIncidents },
          { path: 'audit-logs/:id', element: MyRoomAuditLogs },
        ],
      },
      {
        path: 'contractors',
        element: PageWrapper,
        children: [
          { path: 'create', element: CreateContractorFlow },
          { path: 'information/:id', element: ContractorsInfoLight },
          { path: 'operations/:id', element: ContractorsOperationsLight },
          { path: 'rides/:id', element: RidesPage },
          { path: 'audit-logs/:id', element: MyRoomAuditLogs },
        ],
      },
      {
        path: 'assignment',
        element: PageWrapper,
        children: [{ path: 'information', element: Assignment }],
      },
      {
        path: 'monitoring',
        element: PageWrapper,
        children: [
          {
            path: '',
            element: Monitoring,
          },
        ],
      },
      {
        path: 'standing-order',
        element: PageWrapper,
        children: [
          { path: 'details', element: StandingOrder },
          { path: 'details/:id', element: StandingOrder },
          { path: 'contacts/:id', element: AuthContactsLightNew },
          { path: 'audit-logs/:id', element: MyRoomAuditLogs },
        ],
      },
      {
        path: 'profile',
        element: PageWrapper,
        children: [
          { path: 'user-profile', element: ProfileUserProfile },
          { path: 'audit-logs', element: ProfileAuditLogs },
          { path: 'permissions', element: ProfilePermissions },
          { path: 'recent-activity', element: ProfileRecentActivity },
          { path: '*', element: PageNotFound },
        ],
      },
      {
        path: 'reporting',
        element: PageWrapper,
        children: [
          {
            path: 'ride-reports',
            element: RideReports,
            children: rideHideDashboardRoutes.map((path) => ({
              path,
              element: RideTables,
            })),
          },
          {
            path: 'member-reports',
            element: MemberReports,
            children: memberHideDashboardRoutes.map((path) => ({
              path,
              element: MemberTables,
            })),
          },
          {
            path: 'rideshare-reports',
            element: RideshareReports,
            children: [
              ...contractorDashboardDetailRoutes.map((path) => ({
                path,
                element: RideshareTables,
              })),
              ...contractorDashboardDetailRoutes.map((path) => ({
                path: `${path}/see-rides`,
                element: RideshareSeeRidesTable,
              })),
              ...contractorMonthlyReportRoutes.map((path) => ({
                path,
                element: ContractorMonthlyReportTable,
              })),
              ...contractorMonthlyReportRoutes.map((path) => ({
                path: `${path}/see-rides`,
                element: RideshareSeeRidesTable,
              })),
            ],
          },
          {
            path: 'contractor-rejected-reports',
            element: ContractorRejectedReports,
          },
        ],
      },
    ],
  },
  {
    path: 'icons',
    element: IconsWrapper,
  },
  {
    path: 'configs',
    element: ConfigWrapper,
  },
  {
    path: 'wrong',
    element: SomethingWentWrong,
  },
  {
    path: 'no-authorization',
    element: NoAuthorization,
  },
  {
    path: '*',
    element: PageNotFound,
  },
];
